import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, Card, CardContent, Paper, IconButton, Checkbox, FormControlLabel
} from '@mui/material';
import { seriesApi, squadPlayerApi, matchApi, seriesPointsApi } from '../../services/api';
import type { Series, SquadPlayer, Match, SeriesPoints } from '../../models';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import Grid from '@mui/material/GridLegacy';
import { getTeamShortName } from '../../lib/team'

const SeriesView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [series, setSeries] = useState<Series | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<Partial<Series>>({});
  const [squadPlayers, setSquadPlayers] = useState<SquadPlayer[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [matchCount, setMatchCount] = useState(0);
  const [matchPage, setMatchPage] = useState(1);
  const [matchPageSize, setMatchPageSize] = useState(25);
  const [seriesPoints, setSeriesPoints] = useState<SeriesPoints[]>([]);

  useEffect(() => {
    const fetchSeries = async () => {
      setLoading(true);
      try {
        const res = await seriesApi.getById(Number(id));
        setSeries(res.data);
        setFormValues(res.data);
      } catch (e) {
        setSeries(null);
      } finally {
        setLoading(false);
      }
    };
    fetchSeries();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const fetchSquadPlayers = async () => {
      try {
        const res = await squadPlayerApi.getAll({ page_size: 1000, squad__series: Number(id) });
        setSquadPlayers(res.data.results);
      } catch (e) {
        setSquadPlayers([]);
      }
    };
    fetchSquadPlayers();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const fetchMatches = async () => {
      try {
        const res = await matchApi.getAll({ page: matchPage, page_size: matchPageSize, series: Number(id) });
        setMatches(res.data.results);
        setMatchCount(res.data.count);
      } catch (e) {
        setMatches([]);
      }
    };
    fetchMatches();
  }, [id, matchPage, matchPageSize]);

  useEffect(() => {
    if (!id) return;
    const fetchSeriesPoints = async () => {
      try {
        const res = await seriesPointsApi.getAll({ series: Number(id), page_size: 100 });
        setSeriesPoints(res.data.results);
      } catch (e) {
        setSeriesPoints([]);
      }
    };
    fetchSeriesPoints();
  }, [id]);

  const handleEdit = () => setEditMode(true);
  const handleCancel = () => { setEditMode(false); setFormValues(series || {}); };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormValues(f => ({ 
      ...f, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };
  const handleSave = async () => {
    if (!series) return;
    try {
      await seriesApi.update(series.id, formValues);
      setEditMode(false);
      setSeries({ ...series, ...formValues } as Series);
    } catch (e) {
      alert('Failed to update series');
    }
  };

  // Columns for squad players
  const squadColumns: GridColDef[] = [
    // { field: 'id', headerName: 'ID', width: 60 },
    { field: 'player', headerName: 'Player', width: 180, valueGetter: (value: any) => value?.name },
    { field: 'squad', headerName: 'Team', width: 100, valueGetter: (value: any) => value?.team_name || getTeamShortName(value?.team) },
    { field: 'role', headerName: 'Role', width: 100 },
    { field: 'credits', headerName: 'Cr', width: 50 },
    { field: 'status', headerName: 'Status', width: 180 },
    { field: 'points', headerName: 'Points', width: 60 },
    { field: 'average_points', headerName: 'Avg P', width: 60 },
    { field: 'dt_perc', headerName: 'Dt%', width: 60},
    { field: 'bat_inn', headerName: 'Bat', width: 50 },
    { field: 'bat_pos', headerName: 'Pos', width: 50 },
    { field: 'runs', headerName: 'R', width: 50 },
    { field: 'bowl_inn', headerName: 'Bowl', width: 50 },
    { field: 'overs', headerName: 'Ov', width: 50 },
    { field: 'wickets', headerName: 'W', width: 50 },
  ];

  // Columns for matches
  const matchColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'name', headerName: 'Match', width: 200, renderCell: (params: any) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <a
          href={`https://ai11.in/series/${series?.slug}/match/${params.row.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: 'inherit', fontWeight: 500 }}
        >
          {params.value}
        </a>
        <IconButton
          href={`https://ai11.in/series/${series?.slug}/match/${params.row.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          color="primary"
          sx={{ ml: 0.5 }}
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Box>
    ) },
    { field: 'match_format', headerName: 'Format', width: 80 },
    { field: 'start_date', headerName: 'Date', width: 170, valueGetter: (value: any) => new Date(value).toLocaleString() },
    { field: 'venue', headerName: 'Venue', width: 150, valueGetter: (venue: any) => venue?.name },
    { field: 'match_status', headerName: 'Status', width: 100 },
    { field: 'd_id', headerName: 'D ID', width: 70 },
    { field: 'c_id', headerName: 'C ID', width: 70 },
    { field: 'mt_id', headerName: 'MT ID', width: 70 },
    { field: 'e_id', headerName: 'E ID', width: 70 },
    { field: 'f_id', headerName: 'F ID', width: 70 },
    { field: 'v_id', headerName: 'V ID', width: 70 },
  ];

  const pointsColumns: GridColDef[] = [
    { field: 'group_name', headerName: 'Group', width: 80 },
    { field: 'rank', headerName: 'Rank', width: 70 },
    { field: 'team', headerName: 'Team', width: 160, valueGetter: (value: any) => value?.name || value },
    { field: 'matches_played', headerName: 'M', width: 60 },
    { field: 'matches_won', headerName: 'W', width: 60 },
    { field: 'matches_lost', headerName: 'L', width: 60 },
    { field: 'matches_tied', headerName: 'T', width: 60 },
    { field: 'matches_drawn', headerName: 'D', width: 60 },
    { field: 'points', headerName: 'Pts', width: 70 },
    { field: 'nrr', headerName: 'NRR', width: 80, valueGetter: (value: any) => value?.toFixed(3) || '0.000' },
    { field: 'quotient', headerName: 'Quotient', width: 90, valueGetter: (value: any) => value?.toFixed(3) || '-' },
    { field: 'rpwr', headerName: 'RPWR', width: 90, valueGetter: (value: any) => value?.toFixed(3) || '-' },
    { field: 'for_score', headerName: 'For', width: 100 },
    { field: 'against_score', headerName: 'Against', width: 100 },
  ];

  if (loading) return <Box sx={{ p: 3 }}>Loading...</Box>;
  if (!series) return <Box sx={{ p: 3, color: 'error.main' }}>Series not found</Box>;

  return (
    <Box sx={{ width: '100%', p: { xs: 1, md: 3 } }}>
      <Grid container spacing={3}>
        {/* Summary Card */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>{series.name}</Typography>
                <Button variant="outlined" component={Link} to="/series">Back to Series</Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                <Typography variant="body1"><b>Status:</b> {series.timeline}</Typography>
                <Typography variant="body1"><b>Format:</b> {series.match_format}</Typography>
                <Typography variant="body1"><b>Type:</b> {series.type}</Typography>
                <Typography variant="body1"><b>Start:</b> {series.start_date}</Typography>
                <Typography variant="body1"><b>End:</b> {series.end_date}</Typography>
                <Typography variant="body1"><b>Visible:</b> {series.is_visible ? 'Yes' : 'No'}</Typography>
                <Typography variant="body1"><b>Priority:</b> {series.priority}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" color="primary" onClick={handleEdit} disabled={editMode}>Edit</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Editable Details Section */}
        {editMode && (
          <Grid item xs={12} md={8}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Edit Series Details</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="name" label="Name" value={formValues.name || ''} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="long_name" label="Long Name" value={formValues.long_name || ''} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="abbreviation" label="Abbreviation" value={formValues.abbreviation || ''} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="slug" label="Slug" value={formValues.slug || ''} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="start_date" label="Start Date" type="date" value={formValues.start_date?.split('T')[0] || ''} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="end_date" label="End Date" type="date" value={formValues.end_date?.split('T')[0] || ''} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="timeline" label="Status" value={formValues.timeline || ''} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="match_format" label="Match Format" value={formValues.match_format || ''} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="type" label="Type" value={formValues.type || ''} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="priority" label="Priority" type="number" value={formValues.priority || 0} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="description" label="Description" value={formValues.description || ''} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="League" value={(formValues as any).league_name || 'None'} InputProps={{ readOnly: true }} helperText="Set in Django admin: Series > League" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Organizer" value={(formValues as any).organizer_name || 'Host board / none'} InputProps={{ readOnly: true }} helperText="The series' own, its league's or its tour's; a bilateral tour takes the host board" />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="is_visible"
                          checked={formValues.is_visible || false}
                          onChange={handleChange}
                        />
                      }
                      label="Visible"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Button variant="contained" color="primary" onClick={handleSave}>Save</Button>
                  <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Matches Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Series Matches</Typography>
            <DataGrid
              rows={matches}
              columns={matchColumns}
              autoHeight
              pageSizeOptions={[10, 25, 50, 100]}
              rowCount={matchCount}
              paginationMode="server"
              onPaginationModelChange={(model) => {
                setMatchPage(model.page + 1);
                setMatchPageSize(model.pageSize);
              }}
              density="compact"
              disableRowSelectionOnClick
            />
          </Paper>
        </Grid>

        {/* Squads Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Series Squads</Typography>
            <DataGrid
              rows={squadPlayers}
              columns={squadColumns}
              autoHeight
              pageSizeOptions={[10, 25, 50, 100]}
              density="compact"
              disableRowSelectionOnClick
            />
          </Paper>
        </Grid>

        {/* SeriesPoints Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Series Points</Typography>
            <DataGrid
              rows={seriesPoints}
              columns={pointsColumns}
              autoHeight
              pageSizeOptions={[10, 25, 50, 100]}
              density="compact"
              disableRowSelectionOnClick
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SeriesView; 